import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, ChartOptions } from 'chart.js';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend);

export interface GaugeChartProps {
  value: number; // 0-100 or based on max
  max?: number; // Maximum value (default 100)
  label?: string;
  color?: string;
  height?: number;
  showValue?: boolean;
  thresholds?: {
    // Color zones
    low: { max: number; color: string }; // e.g., 0-60: red
    medium: { max: number; color: string }; // e.g., 60-80: yellow
    high: { color: string }; // e.g., 80-100: green
  };
}

export default function GaugeChart({
  value,
  max = 100,
  label,
  color,
  height = 200,
  showValue = true,
  thresholds,
}: GaugeChartProps) {
  // Determine color based on thresholds
  const getColor = (): string => {
    if (color) return color;
    if (!thresholds) return '#1976d2'; // Default primary blue

    if (value <= thresholds.low.max) return thresholds.low.color;
    if (value <= thresholds.medium.max) return thresholds.medium.color;
    return thresholds.high.color;
  };

  const gaugeColor = getColor();

  const data = {
    datasets: [
      {
        data: [value, max - value],
        backgroundColor: [gaugeColor, 'rgba(0, 0, 0, 0.05)'],
        borderWidth: 0,
        circumference: 180,
        rotation: 270,
      },
    ],
  };

  const options: ChartOptions<'doughnut'> = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '75%',
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
    },
  };

  return (
    <Box
      sx={{
        position: 'relative',
        height,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <div style={{ height: height * 0.7, width: '100%' }}>
        <Doughnut data={data} options={options} />
      </div>
      {showValue && (
        <Box
          sx={{
            position: 'absolute',
            top: height * 0.45,
            textAlign: 'center',
          }}
        >
          <Typography variant="h4" fontWeight="bold">
            {value.toFixed(2)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {label || `of ${max}`}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
